import React from 'react';
import { SportEvent } from '../types/SportEventTypes';
import {
  getAggregatedEventTypes,
  getEventTypeConfig,
} from '../sports/registry';
import type { SportEventTypeConfig } from '../sports/types';

// ============================================================================
// Event Type Configuration — now sourced from per-sport modules under
// ../sports/. The legacy EventTypeConfig type is re-exported for external
// consumers that still type their props against it.
// ============================================================================

export type EventTypeConfig = SportEventTypeConfig;

/**
 * @deprecated Static aggregate of every sport's event-type configs. Prefer
 * `useActiveSportEventTypes()` (from ../sports/hooks) inside components so
 * filter pills are scoped to the current game's sport. Retained as a
 * named export for any non-component caller that still needs the union.
 */
export const EVENT_TYPE_CONFIG: ReadonlyArray<EventTypeConfig> = getAggregatedEventTypes();

/**
 * @deprecated Static aggregate. Inside components, use
 * `useActiveSportEventTypes().backendEventTypes` so only the current sport's
 * events are fetched from the backend.
 */
export const BACKEND_EVENT_TYPES: string[] = EVENT_TYPE_CONFIG
  .filter((e) => e.fetchFromBackend)
  .map((e) => e.type);

/**
 * @deprecated Static aggregate. Inside components, use
 * `useActiveSportEventTypes().filterableEventTypes` for sport-scoped pills.
 */
export const FILTERABLE_EVENT_TYPES: EventTypeConfig[] = EVENT_TYPE_CONFIG
  .filter((e) => e.showFilterPill)
  .slice();

// ============================================================================
// Per-event-type lookups
//
// These work across sports because event-type strings are globally unique
// (NHL owns 'goal'/'shot'/...; NBA owns 'basket_made_3pt'/'rebound_*'/...).
// The registry scans every module to find the owning config, so callers
// don't need to know which sport an event belongs to.
// ============================================================================

/** Hide the team logo cell for events with no team affiliation (period markers, custom events). */
export const shouldHideTeamLogo = (eventType: string): boolean => {
  return getEventTypeConfig(eventType)?.hideTeamLogo ?? false;
};

/** Icon component to render in place of the team logo (e.g. whistle for icing/violations). */
export const getEventTypeIcon = (eventType: string): React.FC<{ size?: number }> | undefined => {
  return getEventTypeConfig(eventType)?.icon;
};

/** Human label for an event type (e.g. "goal" -> "Goal", "basket_made_3pt" -> "3-Pointer Made"). */
export const getEventTypeDisplayName = (eventType: string): string => {
  const config = getEventTypeConfig(eventType);
  return config?.displayName || eventType.replace(/_/g, ' ');
};

/** Whether an event type should appear as a filter pill in the panel header. */
export const displayEventTypesInTheFilter = (eventType: string): boolean => {
  return getEventTypeConfig(eventType)?.showFilterPill ?? false;
};

/** @deprecated Use BACKEND_EVENT_TYPES instead (or, in components, the hook). */
export const ENABLED_EVENT_TYPES: string[] = BACKEND_EVENT_TYPES;

// ============================================================================
// Sport-aware helpers
//
// These have semantic meaning that varies per sport but expose a uniform
// interface. Today: a "highlight scoring event" is a hockey goal OR a
// successful NBA field-goal.
// ============================================================================

/**
 * True for events that should render with the colored, full-width
 * scoreboard-style "highlight" block (GoalEventItem). Today this is hockey
 * goals only.
 *
 * NBA scoring plays (2pt/3pt baskets, made free throws) deliberately do NOT
 * qualify: per the NBA design they stay inline in the normal timeline and get
 * the running score annotated on the right via ScoreEventItem, which the NBA
 * module selects through its `pickRenderer` hook. They're still scoring events
 * for score purposes — see getScore() below, which keeps reading their
 * home_score/away_score.
 */
export const isGoalEvent = (event: SportEvent): boolean => {
  const type = (event.payload as any)?.type;
  return type === 'goal';
};

/** Period boundary marker (shared shape across sports — both NHL and NBA emit period_start/period_end). */
export const isPeriodEvent = (event: SportEvent): boolean => {
  const type = (event.payload as any)?.type;
  return type === 'period_start' || type === 'period_end';
};

/**
 * Extract the running score from any scoring event. Works for both NHL
 * goals and NBA scoring events (baskets + free throws) since SES uses the
 * same `home_score`/`away_score` field names on the payload.data block.
 * Returns null for non-scoring events so callers can short-circuit.
 */
export const getScore = (event: SportEvent): { home: number; away: number } | null => {
  const payload = event.payload as any;
  const type = payload?.type;
  const SCORING_TYPES = new Set([
    'goal',
    'basket_made_2pt',
    'basket_made_3pt',
    'free_throw_made',
  ]);
  if (!SCORING_TYPES.has(type)) return null;
  return {
    home: payload?.data?.home_score || 0,
    away: payload?.data?.away_score || 0,
  };
};

// ============================================================================
// Sport-agnostic utility functions (formatting, sorting, colors)
// ============================================================================

export interface PlayByPlayPanelProps {
  width?: number;
  onCreateClip?: (
    event: any,
    metadata: { teamLogoPath: string; gameTime: string; description: string },
  ) => void;
}

export const formatGameTime = (timeInSeconds: number | string): string => {
  timeInSeconds = Number(timeInSeconds);
  if (!timeInSeconds || timeInSeconds < 0) return '--:--';
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  if (minutes === 0 && seconds <= 10) {
    return timeInSeconds.toFixed(1);
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const getPeriodNumber = (encodedPeriod: number): number => encodedPeriod;

export const getContrastTextColor = (bgColor: string): string => {
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Optimized player sorting by jersey number
export const sortPlayersByNumber = (
  a: { number?: number | string },
  b: { number?: number | string },
): number => {
  const numA = Number(a.number) || 999;
  const numB = Number(b.number) || 999;
  return numA - numB;
};
